import { useEffect, useMemo, useState, type CSSProperties, type DragEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Lead, LeadStatus } from '@neuroedge/shared';
import { Button, Card, PageState, Select, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { usePipelines } from '../../api/pipelines';
import { useLeads, useUpdateLead, useMoveLead } from '../../api/leads';
import { useTags } from '../../api/tags';
import { TagChips } from '../tags/TagChips';
import { LeadDetailModal } from './LeadDetailModal';
import { LeadsGrid } from './LeadsGrid';

type LeadsView = 'list' | 'board';
const VIEW_STORAGE_KEY = 'ne.leads.view';

function loadView(): LeadsView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'board' ? 'board' : 'list';
  } catch {
    return 'list';
  }
}

/** Custom MIME type (not `text/plain`) so the drag payload is never
 * mistaken for a stray text drag from elsewhere on the page. */
const LEAD_DRAG_TYPE = 'application/x-neuroedge-lead-id';

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'HOLD', label: 'Hold' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
  { value: 'ABANDONED', label: 'Abandoned' },
];

/** The statuses someone still needs to act on - the default view, so a
 * board full of old Won/Lost/Abandoned leads doesn't bury the ones that
 * actually need attention. */
const ACTIVE_STATUSES: LeadStatus[] = ['OPEN', 'HOLD'];
const ALL_STATUSES: LeadStatus[] = STATUS_OPTIONS.map((o) => o.value);

function sameStatusSet(a: Set<LeadStatus>, statuses: LeadStatus[]): boolean {
  return a.size === statuses.length && statuses.every((s) => a.has(s));
}

function statusChipStyle(active: boolean): CSSProperties {
  return {
    cursor: 'pointer',
    border: active ? 'none' : '1px solid var(--color-border)',
    background: active ? 'var(--color-primary)' : 'var(--color-surface)',
    color: active ? 'var(--color-primary-contrast)' : 'var(--color-text-muted)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-1) var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: active ? 600 : 400,
  };
}

function LeadCard({
  lead,
  canManage,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
}: {
  lead: Lead;
  canManage: boolean;
  dragging: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onOpen: () => void;
}) {
  const updateStatus = useUpdateLead(lead.id);
  const toast = useToast();
  return (
    <Card
      draggable={canManage}
      onDragStart={canManage ? onDragStart : undefined}
      onDragEnd={canManage ? onDragEnd : undefined}
      style={{
        padding: 'var(--space-3)',
        cursor: canManage ? 'grab' : undefined,
        opacity: dragging ? 0.4 : 1,
      }}
    >
      <button
        onClick={onOpen}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        {lead.title}
      </button>
      <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
        {lead.contact.displayName}
        {lead.valueAmount != null ? ` · ${lead.currency} ${lead.valueAmount}` : ''}
      </div>
      {lead.contact.phone ? (
        <a
          href={`tel:${lead.contact.phone}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-1)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-primary)',
            textDecoration: 'none',
          }}
        >
          📞 {lead.contact.phone}
        </a>
      ) : null}
      {lead.tags && lead.tags.length > 0 ? (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <TagChips tags={lead.tags} />
        </div>
      ) : null}
      {canManage ? (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            marginTop: 'var(--space-2)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
          }}
        >
          Status
          <select
            className="ne-input"
            style={{ fontSize: 'var(--font-size-sm)', flex: 1 }}
            value={lead.status}
            disabled={updateStatus.isPending}
            onChange={(e) =>
              updateStatus.mutate(
                { status: e.target.value as LeadStatus },
                {
                  onSuccess: () => toast.success('Lead status updated.'),
                  onError: (err) =>
                    toast.error(err instanceof Error ? err.message : 'Could not update status.'),
                },
              )
            }
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <span className="ne-badge" style={{ marginTop: 'var(--space-2)', display: 'inline-block' }}>
          {lead.status}
        </span>
      )}
    </Card>
  );
}

export function LeadsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const pipelines = usePipelines();
  const tags = useTags();
  const moveLead = useMoveLead();
  const [pipelineId, setPipelineId] = useState<string>('');
  const [tagId, setTagId] = useState<string>('');
  const [draggingLeadId, setDraggingLeadId] = useState<string | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);
  const [view, setViewState] = useState<LeadsView>(loadView);
  const setView = (next: LeadsView) => {
    setViewState(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Per-viewer convenience only - the default view is used next time.
    }
  };
  const [selectedStatuses, setSelectedStatuses] = useState<Set<LeadStatus>>(
    () => new Set(ACTIVE_STATUSES),
  );

  function toggleStatus(status: LeadStatus) {
    setSelectedStatuses((prev) => {
      if (prev.has(status) && prev.size === 1) return prev; // keep at least one selected
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }

  useEffect(() => {
    if (!pipelineId && pipelines.data?.length) {
      setPipelineId(pipelines.data.find((p) => p.isDefault)?.id ?? pipelines.data[0]!.id);
    }
  }, [pipelines.data, pipelineId]);

  const pipeline = pipelines.data?.find((p) => p.id === pipelineId);
  // The board loads a whole pipeline at once; the list pages server-side (LeadsGrid).
  const leads = useLeads(
    { pipelineId: pipelineId || undefined, tagId: tagId || undefined, pageSize: 500 },
    view === 'board',
  );

  const statusCounts = useMemo(() => {
    const counts = new Map<LeadStatus, number>();
    for (const lead of leads.data?.items ?? []) {
      counts.set(lead.status, (counts.get(lead.status) ?? 0) + 1);
    }
    return counts;
  }, [leads.data]);

  const byStage = useMemo(() => {
    const map = new Map<string, Lead[]>();
    for (const lead of leads.data?.items ?? []) {
      if (!selectedStatuses.has(lead.status)) continue;
      const arr = map.get(lead.stage.id) ?? [];
      arr.push(lead);
      map.set(lead.stage.id, arr);
    }
    return map;
  }, [leads.data, selectedStatuses]);

  const canManage = hasPermission('leads.update');

  function onDropOnStage(e: DragEvent<HTMLDivElement>, stageId: string) {
    e.preventDefault();
    setDragOverStageId(null);
    const leadId = e.dataTransfer.getData(LEAD_DRAG_TYPE);
    if (!leadId) return;
    const lead = leads.data?.items.find((l) => l.id === leadId);
    if (!lead || lead.stage.id === stageId) return; // dropped back on its own column - no-op
    moveLead.mutate(
      { id: leadId, stageId },
      {
        onSuccess: () => toast.success('Lead moved.'),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not move the lead.'),
      },
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Leads</h1>
        {pipelines.data && pipelines.data.length > 0 ? (
          <Select
            label="Pipeline"
            value={pipelineId}
            options={pipelines.data.map((p) => ({ value: p.id, label: p.name }))}
            onChange={(e) => setPipelineId(e.target.value)}
          />
        ) : null}
        <Select
          label="Tag"
          value={tagId}
          options={[
            { value: '', label: 'Any tag' },
            ...(tags.data ?? []).map((t) => ({ value: t.id, label: t.name })),
          ]}
          onChange={(e) => setTagId(e.target.value)}
        />
        <div role="group" aria-label="View" style={{ display: 'flex', gap: 'var(--space-1)' }}>
          <Button
            variant={view === 'list' ? 'primary' : 'secondary'}
            aria-pressed={view === 'list'}
            onClick={() => setView('list')}
          >
            List
          </Button>
          <Button
            variant={view === 'board' ? 'primary' : 'secondary'}
            aria-pressed={view === 'board'}
            onClick={() => setView('board')}
          >
            Board
          </Button>
        </div>
        <span style={{ flex: 1 }} />
        {hasPermission('leads.create') ? (
          <Button onClick={() => navigate('/leads/new')}>New lead</Button>
        ) : null}
      </div>

      {view === 'list' ? (
        pipelines.isError ? (
          <PageState kind="error" message={pipelines.error.message} />
        ) : (
          <Card>
            <LeadsGrid
              pipeline={pipeline}
              tagId={tagId || undefined}
              onOpen={(id) => setOpenLeadId(id)}
            />
          </Card>
        )
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
              Show:
            </span>
            <button
              type="button"
              style={statusChipStyle(sameStatusSet(selectedStatuses, ACTIVE_STATUSES))}
              onClick={() => setSelectedStatuses(new Set(ACTIVE_STATUSES))}
            >
              Active
            </button>
            <button
              type="button"
              style={statusChipStyle(sameStatusSet(selectedStatuses, ALL_STATUSES))}
              onClick={() => setSelectedStatuses(new Set(ALL_STATUSES))}
            >
              All
            </button>
            <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--color-border)' }} />
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                style={statusChipStyle(selectedStatuses.has(o.value))}
                onClick={() => toggleStatus(o.value)}
              >
                {o.label} ({statusCounts.get(o.value) ?? 0})
              </button>
            ))}
          </div>

          {pipelines.isPending || leads.isPending ? (
            <PageState kind="loading" message="Loading leads…" />
          ) : pipelines.isError ? (
            <PageState kind="error" message={pipelines.error.message} />
          ) : leads.isError ? (
            <PageState
              kind="error"
              message={leads.error.message}
              onRetry={() => void leads.refetch()}
            />
          ) : !pipeline ? (
            <PageState kind="empty" title="No pipeline" message="Create a pipeline first." />
          ) : (leads.data?.items.length ?? 0) > 0 &&
            Array.from(byStage.values()).every((arr) => arr.length === 0) ? (
            <PageState
              kind="empty"
              title="No leads match this filter"
              message='Try "All" above to see every status.'
            />
          ) : (
            <div
              style={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridAutoColumns: 'minmax(240px, 1fr)',
                gap: 'var(--space-3)',
                overflowX: 'auto',
              }}
            >
              {pipeline.stages.map((stage) => {
                const items = byStage.get(stage.id) ?? [];
                const isDragOver = dragOverStageId === stage.id;
                return (
                  <div
                    key={stage.id}
                    onDragOver={(e) => {
                      if (!canManage) return;
                      e.preventDefault();
                      if (dragOverStageId !== stage.id) setDragOverStageId(stage.id);
                    }}
                    onDragLeave={() => setDragOverStageId((id) => (id === stage.id ? null : id))}
                    onDrop={canManage ? (e) => onDropOnStage(e, stage.id) : undefined}
                    style={{
                      display: 'grid',
                      gap: 'var(--space-2)',
                      alignContent: 'start',
                      minHeight: 80,
                      borderRadius: 'var(--radius-md)',
                      outline: isDragOver ? '2px dashed var(--color-primary)' : 'none',
                      outlineOffset: 2,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      {stage.name} ({items.length})
                    </div>
                    {items.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        canManage={canManage}
                        dragging={draggingLeadId === lead.id}
                        onDragStart={(e) => {
                          e.dataTransfer.setData(LEAD_DRAG_TYPE, lead.id);
                          e.dataTransfer.effectAllowed = 'move';
                          setDraggingLeadId(lead.id);
                        }}
                        onDragEnd={() => {
                          setDraggingLeadId(null);
                          setDragOverStageId(null);
                        }}
                        onOpen={() => setOpenLeadId(lead.id)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>

      {openLeadId ? <LeadDetailModal id={openLeadId} onClose={() => setOpenLeadId(null)} /> : null}
    </div>
  );
}
